import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Gift, CreditCard, Percent, Shield, Zap, Star, Plane, PiggyBank, Smartphone, Wallet, TrendingUp, Heart } from 'lucide-react';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png";

const PRODUCTS = [
  {
    icon: Gift,
    title: "Partner Rewards",
    desc: "Earn loyalty points every time you shop at our 25+ partner stores. Redeem for cash, vouchers, or airtime.",
    features: ["Cashback on every purchase", "Points never expire", "Instant digital vouchers"],
    color: "from-amber-500 to-amber-600",
    bgLight: "bg-amber-50",
    iconColor: "text-amber-600"
  },
  {
    icon: Percent,
    title: "Discount Vouchers",
    desc: "Access exclusive discounts of up to 40% at leading grocery, fuel, and retail stores across South Africa.",
    features: ["Up to 40% off groceries", "Fuel discounts nationwide", "Airtime & data deals"],
    color: "from-emerald-500 to-emerald-600",
    bgLight: "bg-emerald-50",
    iconColor: "text-emerald-600"
  },
  {
    icon: CreditCard,
    title: "Diamond Card",
    desc: "Our premium digital banking card that gives you instant payments, transfers, and full financial control.",
    features: ["Contactless payments", "Free inter-account transfers", "Real-time notifications"],
    color: "from-sky-500 to-sky-600",
    bgLight: "bg-sky-50",
    iconColor: "text-sky-600"
  },
  {
    icon: Shield,
    title: "Insurance Cover",
    desc: "Protect your family and assets with affordable funeral, life, medical, vehicle, pet, and house insurance.",
    features: ["Funeral cover from R49/mo", "Life & medical insurance", "Vehicle & home protection"],
    color: "from-violet-500 to-violet-600",
    bgLight: "bg-violet-50",
    iconColor: "text-violet-600"
  },
  {
    icon: Zap,
    title: "Quick Loans",
    desc: "Same-day payday loans from R500 to R8,000 with transparent terms and competitive interest rates.",
    features: ["Approval within hours", "No hidden fees", "Flexible repayment"],
    color: "from-orange-500 to-orange-600",
    bgLight: "bg-orange-50",
    iconColor: "text-orange-600"
  },
  {
    icon: Plane,
    title: "Travel Deals",
    desc: "Book affordable domestic and international flights, accommodation, and holiday packages through our platform.",
    features: ["Discounted flights", "Hotel deals nationwide", "Package holidays"],
    color: "from-cyan-500 to-cyan-600",
    bgLight: "bg-cyan-50",
    iconColor: "text-cyan-600"
  },
];

const EXTRAS = [
  { icon: PiggyBank, title: "Savings Tools", desc: "Set goals and watch your money grow" },
  { icon: Smartphone, title: "Mobile Access", desc: "Full banking from your phone" },
  { icon: Wallet, title: "E-Wallet", desc: "Send and receive money instantly" },
  { icon: TrendingUp, title: "Financial Insights", desc: "AI-powered spending analysis" },
  { icon: Heart, title: "Wellness Benefits", desc: "Healthcare discounts for members" },
  { icon: Star, title: "VIP Tiers", desc: "Earn more as your loyalty grows" },
];

export const ProductsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={LOGO_URL} alt="AfriPerx" className="h-10 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm text-gray-600 hover:text-afriperx-green font-medium transition-colors">Home</Link>
            <Link to="/about" className="text-sm text-gray-600 hover:text-afriperx-green font-medium transition-colors">About Us</Link>
            <Link to="/products" className="text-sm text-afriperx-green font-semibold">Our Products</Link>
            <Link to="/contact" className="text-sm text-gray-600 hover:text-afriperx-green font-medium transition-colors">Contact Us</Link>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/login')} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors">Sign In</button>
            <button onClick={() => navigate('/get-started')} className="px-5 py-2 text-sm bg-afriperx-green hover:bg-afriperx-green-dark text-white rounded-full font-medium transition-colors">Get Started</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center pt-8">
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold text-afriperx-gold bg-amber-100 px-4 py-1.5 rounded-full mb-4">Our Products</span>
          <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">Everything You Need, <span className="text-afriperx-gold">One Platform</span></h1>
          <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
            From rewards and discounts to banking and insurance — AfriPerx gives you the tools to save more, earn more, and live better.
          </p>
        </div>
      </section>

      {/* Main Products */}
      <section className="py-14 px-4">
        <div className="max-w-6xl mx-auto space-y-8">
          {PRODUCTS.map((p, i) => (
            <div key={i} className={`rounded-3xl border border-gray-100 overflow-hidden hover:shadow-xl hover:shadow-afriperx-gold/5 transition-all duration-300 ${i % 2 === 0 ? '' : ''}`}>
              <div className="flex flex-col md:flex-row">
                <div className={`md:w-1/3 bg-gradient-to-br ${p.color} p-8 flex flex-col items-center justify-center text-white`}>
                  <p.icon className="w-16 h-16 mb-4 opacity-90" />
                  <h3 className="font-syne text-2xl font-bold text-center">{p.title}</h3>
                </div>
                <div className="md:w-2/3 p-8">
                  <p className="text-gray-600 leading-relaxed mb-4">{p.desc}</p>
                  <div className="space-y-2">
                    {p.features.map((f, fi) => (
                      <div key={fi} className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full ${p.bgLight} flex items-center justify-center`}>
                          <Star className={`w-3 h-3 ${p.iconColor}`} />
                        </div>
                        <span className="text-gray-700 text-sm">{f}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => navigate('/get-started')} className="mt-6 px-6 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm rounded-full font-medium transition-colors inline-flex items-center gap-2">
                    Get This Product <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Extra Features */}
      <section className="py-14 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center mb-10">
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold text-afriperx-green bg-green-50 px-4 py-1.5 rounded-full mb-4">Coming Soon</span>
          <h2 className="font-syne text-2xl md:text-3xl font-bold text-gray-900">More Features On The Way</h2>
        </div>
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-4">
          {EXTRAS.map((e, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 hover:border-afriperx-gold/20 hover:shadow-md transition-all duration-300 text-center">
              <div className="w-11 h-11 rounded-xl bg-afriperx-green/10 flex items-center justify-center mx-auto mb-3">
                <e.icon className="w-5 h-5 text-afriperx-green" />
              </div>
              <h3 className="font-syne font-bold text-gray-900 text-sm mb-1">{e.title}</h3>
              <p className="text-gray-500 text-xs">{e.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-afriperx-green to-afriperx-green-dark rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-afriperx-gold/10 rounded-full blur-3xl" />
            <div className="relative z-10">
              <h2 className="font-syne text-2xl md:text-3xl font-bold text-white mb-3">Start Earning Today</h2>
              <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-6">Join 208 000+ members and unlock exclusive rewards and financial tools.</p>
              <button onClick={() => navigate('/get-started')} className="px-8 py-3.5 bg-afriperx-gold hover:bg-afriperx-gold-dark text-white font-syne font-bold rounded-full transition-colors text-sm inline-flex items-center gap-2" data-testid="products-cta-btn">
                Get Started <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={LOGO_URL} alt="AfriPerx" className="h-8 w-auto brightness-200" />
            <div className="flex gap-6">
              <Link to="/" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Home</Link>
              <Link to="/about" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">About</Link>
              <Link to="/contact" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Contact</Link>
              <Link to="/terms" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Terms</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-6 pt-6 text-center">
            <p className="text-gray-600 text-xs">&copy; 2026 AfriPerx. All rights reserved. FSP Licensed.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
